import React, { useState } from "react";
import axios from "axios";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = ["possible_fraud", "false_info", "discrimination", "inappropriate_content", "job_expired", "other"];

// Trust & Safety MVP (launch-readiness sprint, sept-2026) — simple entry point,
// the real moderation action happens later in Admin (pause job / suspend company).
export default function ReportJobModal({ jobId, onClose }) {
  const { t } = useLanguage();
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!reason) return;
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/jobs/${jobId}/report`, { reason, details: details || null }, { headers: getAuthHeaders() });
      setSent(true);
    } catch {
      setError(t("jobDetail.reportError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-end sm:items-center justify-center" data-testid="report-job-modal" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-poppins font-bold text-slate-900 text-lg">{t("jobDetail.reportTitle")}</h3>
          <button onClick={onClose} data-testid="report-job-modal-close" className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {sent ? (
          <div className="flex flex-col items-center text-center gap-3 py-6" data-testid="report-job-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="text-sm text-slate-600">{t("jobDetail.reportSent")}</p>
            <button onClick={onClose} className="mt-2 h-10 px-6 bg-slate-900 text-white text-sm font-semibold rounded-xl">
              {t("common.close")}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-4">
              {REASONS.map((r) => (
                <label
                  key={r}
                  className={`flex items-center gap-2 border rounded-xl px-3 py-2.5 text-sm cursor-pointer transition-all ${reason === r ? "border-[#028CF8] bg-[#028CF8]/5 text-slate-900" : "border-slate-200 text-slate-600"}`}
                >
                  <input type="radio" name="report-reason" value={r} checked={reason === r} onChange={() => setReason(r)} className="accent-[#028CF8]" data-testid={`report-reason-${r}`} />
                  {t(`jobDetail.reportReasons.${r}`)}
                </label>
              ))}
            </div>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={t("jobDetail.reportDetailsPlaceholder")}
              rows={3}
              data-testid="report-details-textarea"
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] mb-3"
            />
            {error && <p className="text-xs text-red-500 mb-3" data-testid="report-job-error">{error}</p>}
            <button
              onClick={submit}
              disabled={!reason || sending}
              data-testid="report-job-submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-semibold rounded-xl text-sm flex items-center justify-center gap-2"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("jobDetail.reportSubmit")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
